import React, {useMemo} from "react";
import {useNavigate} from "react-router-dom";

import {getNeuronItemId} from "../utils/getNeuronItemId";
import SectionHeading from "./SectionHeading";
import {Skeleton} from "./ui/skeleton";

/**
 * "In brief" — the dense column.
 *
 * The front page above this is deliberately airy: one lead, big picture, room
 * to breathe. This block is its opposite, and that contrast is the point. A
 * newspaper alternates rhythm so the eye keeps moving; a page of identically
 * sized cards is the sound of nobody deciding. Here the picture is gone
 * entirely, headlines run tight in four columns divided by hairlines, and the
 * dateline does the ordering.
 */
const LatestArticles = ({
  stories = [],
  isLoading = false,
  limit = 12,
  viewInterceptor,
  viewAllTo = "/stories",
}) => {
  const navigate = useNavigate();

  const latestStories = useMemo(() => {
    if (!Array.isArray(stories)) return [];
    const ts = (story) => {
      const t = story?.createdDate ? new Date(story.createdDate).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    return [...stories]
      .filter(Boolean)
      .sort((a, b) => ts(b) - ts(a))
      .slice(0, limit);
  }, [stories, limit]);

  const formatDate = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString("en-GB", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (!latestStories.length && !isLoading) return null;

  return (
    <section className="container py-8">
      <SectionHeading
        kicker="Latest"
        title="In brief"
        tone="editorial"
        standfirst="Everything published today, newest first."
        action={
          <button
            type="button"
            onClick={() => navigate(viewAllTo)}
            className="font-sans text-meta font-semibold uppercase tracking-[0.08em] text-ink-tertiary underline-offset-4 transition-colors duration-150 ease-out hover:text-masthead hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            View all
          </button>
        }
      />

      {isLoading ? (
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex flex-col gap-2 border-t border-rule pt-3">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      ) : (
        <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
          {latestStories.map((story) => {
            const title = story.originalTitle || story.aiTitle || "Untitled";
            return (
              <li key={story.uuid} className="border-t border-rule">
                <button
                  type="button"
                  ref={viewInterceptor?.getItemRef(getNeuronItemId(story))}
                  onClick={() => navigate(`/article/${story.uuid}`)}
                  className="group w-full py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                >
                  <span className="mb-1 block font-sans text-kicker uppercase tracking-[0.09em] text-ink-muted">
                    {formatDate(story.createdDate)}
                  </span>
                  <h3 className="font-serif text-h4 font-semibold leading-snug text-ink decoration-masthead/40 underline-offset-[3px] line-clamp-3 group-hover:underline">
                    {title}
                  </h3>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default LatestArticles;
